import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCookie, removeCookie } from '../utils/cookies';
import './Signup.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const Signup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isInvitedFlow = searchParams.get('invitation') === 'true';
  const invitationToken = searchParams.get('token');
  const invitationEmail = searchParams.get('email');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: isInvitedFlow ? invitationEmail : '',
    password: '',
    confirmPassword: '',
    societyId: '',
    roomNo: ''
  });
  
  const [societies, setSocieties] = useState([]);
  const [roomNumbers, setRoomNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch societies on component mount
  useEffect(() => {
    const fetchSocieties = async () => {
      try {
        const response = await fetch(`${API_URL}/api/v1/auth/orgs`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch societies');
        }

        const result = await response.json();
        
        if (result.status && result.data) {
          console.log('Societies data:', result.data);
          setSocieties(result.data);
        } else {
          throw new Error(result.message || 'Failed to fetch societies');
        }
      } catch (error) {
        console.error('Error fetching societies:', error);
        setError('Failed to load societies. Please try again.');
      }
    };

    fetchSocieties();
  }, []);

  // Fetch room numbers when society is selected
  useEffect(() => {
    const fetchRoomNumbers = async () => {
      if (!formData.societyId) return;

      try {
        const response = await fetch(`${API_URL}/api/v1/auth/rooms?org_id=${formData.societyId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch room numbers');
        }

        const result = await response.json();
        
        if (result.status && result.data) {
          setRoomNumbers(result.data.RoomNos);
        } else {
          throw new Error(result.message || 'Failed to fetch room numbers');
        }
      } catch (error) {
        console.error('Error fetching room numbers:', error);
        setError('Failed to load room numbers. Please try again.');
      }
    };

    fetchRoomNumbers();
  }, [formData.societyId]);

  useEffect(() => {
    // Clean up cookies on unmount
    return () => {
      if (isInvitedFlow) {
        removeCookie('invitationToken');
        removeCookie('invitationEmail');
      }
    };
  }, [isInvitedFlow]);

  useEffect(() => {
    // Check cookies on mount
    if (isInvitedFlow) {
      const token = getCookie('invitationToken');
      const email = getCookie('invitationEmail');
      console.log('Cookies on mount:', { token, email });
    }
  }, [isInvitedFlow]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear room number when society changes
    if (name === 'societyId') {
      setFormData(prev => ({
        ...prev,
        roomNo: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isInvitedFlow) {
        if (!invitationToken) {
          throw new Error('Invalid invitation. Please use the invitation link sent to your email.');
        }

        const signupData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          confirm_password: formData.confirmPassword,
          invitation_token: invitationToken
        };

        const response = await fetch(`${API_URL}/api/v1/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(signupData),
        });

        const data = await response.json();
        console.log('Registration response:', data);

        if (!response.ok) {
          throw new Error(data.message || 'Registration failed');
        }

        navigate('/login', { 
          state: { 
            message: 'Account created successfully! Please login to continue.' 
          }
        });
      } else {
        const signupData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          confirm_password: formData.confirmPassword,
        };

        // Add society and room fields only if not invited flow
        signupData.societyId = formData.societyId;
        signupData.roomNo = formData.roomNo;

        const response = await fetch(`${API_URL}/api/v1/auth/signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(signupData),
        });

        const data = await response.json();

        if (response.ok) {
          navigate('/login', { 
            state: { 
              message: 'Account created successfully! Please login to continue.' 
            }
          });
        } else {
          throw new Error(data.message || 'Registration failed');
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-image-section">
          <div className="auth-image-overlay">
            <h1>Welcome to Society Connect</h1>
            <p>Managing your society living experience, simplified.</p>
          </div>
        </div>
        <div className="auth-form-section">
          <div className="auth-form-container">
            <h2>{isInvitedFlow ? 'Complete Your Registration' : 'Create Account'}</h2>
            <p className="auth-subtitle">Join your society's digital community</p>
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    placeholder="Enter your first name"
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    placeholder="Enter your last name"
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isInvitedFlow}
                  className={`form-control ${isInvitedFlow ? 'disabled' : ''}`}
                />
              </div>

              {/* Only show society and room fields if not invited flow */}
              {!isInvitedFlow && (
                <>
                  <div className="form-group">
                    <label htmlFor="societyId">Society</label>
                    <select
                      id="societyId"
                      name="societyId"
                      value={formData.societyId}
                      onChange={handleChange}
                      required
                      className="form-control"
                    >
                      <option value="">Select your society</option>
                      {societies && societies.map(society => (
                        <option key={society.ID || society.Id} value={society.ID || society.Id}>
                          {society.Name || society.name || 'Unknown Society'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="roomNo">Room Number</label>
                    <select
                      id="roomNo"
                      name="roomNo"
                      value={formData.roomNo}
                      onChange={handleChange}
                      required
                      className="form-control"
                      disabled={!formData.societyId}
                    >
                      <option value="">Select your room number</option>
                      {roomNumbers && roomNumbers.map(roomNo => (
                        <option key={roomNo} value={roomNo}>
                          {roomNo}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="Enter your password"
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  placeholder="Confirm your password"
                  className="form-control"
                />
              </div>

              {!isInvitedFlow && (
                <div className="required-fields-note">* Required fields</div>
              )}

              <button type="submit" className="auth-button" disabled={loading}>
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
            
            <div className="auth-footer">
              <p>Already have an account? 
                <button onClick={() => navigate('/login')} className="auth-link">
                  Login
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup; 